/** @file sheet.upload: stores a sheet image and points the tiles of its cells at it (sheet-backed strips). */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import type { FacetEntry } from '@zilo/pose3d-format/types/facet';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import { checkRecord } from '@zilo/pose3d-format/validate/fields/checkRecord';
import { checkString } from '@zilo/pose3d-format/validate/fields/checkString';
import { checkTuple } from '@zilo/pose3d-format/validate/fields/checkTuple';
import { NAME_PATTERN } from '@zilo/pose3d-format/validate/fields/namePattern';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import type { CommandDef } from '../apply/commandDef';
import { puppetInverse } from '../apply/puppetInverse';
import { checkPuppet } from '../puppetDoc/checkPuppet';
import { findSlot } from '../slot/findSlot';
import { compact } from '../validate/compact';
import { pointerToken } from '../validate/pointerToken';
import { readInput } from '../validate/readInput';
import { stripOrigins } from './stripOrigins';

const sheetName = checkString(NAME_PATTERN);
const cellsCheck = checkRecord(checkTuple(2, { int: true, min: 0 }), /^.+$/);
const pixelSize = checkTuple(2, { int: true, min: 1 });

/** Every cell's tile rect must lie inside the sheet. */
function checkBounds(p: Puppet, cells: Record<string, [number, number]>, size: [number, number]): void {
  for (const [key, [x, y]] of Object.entries(cells)) {
    const path = `/input/cells/${pointerToken(key)}`;
    const [w, h] = findSlot(p, key, path).entry.size;
    if (x + w > size[0] || y + h > size[1]) throwError(ERROR_CODES.VALIDATION, { cell: [x, y, w, h], sheet: size }, { path, hint: 'the tile rect lies outside the sheet' });
  }
}

/** Every impostor entry with the image of the sheet it named before the upload. */
function sheetImages(p: Puppet): Map<FacetEntry, string | undefined> {
  const entries = p.drawables.flatMap((d) => (d.kind === 'impostor' ? d.facets : []));
  return new Map(entries.map((e) => [e, p.sheets[e.sheet]?.image]));
}

/** Tiles of entries now on `sheet` that named their old sheet image are cut from the new image (re-upload, moves). */
function repoint(before: ReadonlyMap<FacetEntry, string | undefined>, sheet: string, image: string): void {
  for (const [entry, old] of before) {
    if (entry.sheet !== sheet || old === undefined || old === image) continue;
    for (const [t, img] of Object.entries(entry.slots)) if (img === old) entry.slots[t] = image;
  }
}

/**
 * `sheet.upload { sheet, image, size, cells? }` → `{ sheet, entries }`: `size` must be the image's size; each entry
 * named by `cells` moves to `sheet` at the strip origin its cells imply, and every touched tile names the sheet image.
 * Loose images nothing references any more leave the document. Inverse P.
 */
export const sheetUploadCommand: CommandDef<'sheet.upload'> = {
  name: 'sheet.upload',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => {
    const r = readInput(input, path, ['sheet', 'image', 'size', 'cells']);
    return compact({
      sheet: r.check('sheet', sheetName) as string, image: r.str('image'), size: r.check('size', pixelSize) as [number, number],
      cells: r.opt('cells', (k) => r.check(k, cellsCheck) as Record<string, [number, number]>),
    });
  },
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input, ctx) => {
    const p = draft.puppet;
    const meta = ctx.images[input.image] ?? throwError(ERROR_CODES.NOT_FOUND, { image: input.image }, { path: '/input/image', hint: 'upload the image first' });
    if (meta.size[0] !== input.size[0] || meta.size[1] !== input.size[1]) {
      throwError(ERROR_CODES.VALIDATION, { expected: meta.size, actual: input.size }, { path: '/input/size', hint: 'the size of the uploaded image' });
    }
    const cells = input.cells ?? {};
    const origins = stripOrigins(p, cells);
    checkBounds(p, cells, input.size);
    const inverse = puppetInverse(draft);
    const before = sheetImages(p);
    p.sheets[input.sheet] = { image: input.image, size: [...input.size] };
    for (const [id, origin] of origins) {
      const [drawable, index] = id.split('/');
      const d = p.drawables.find((x) => x.id === drawable);
      const entry = d?.kind === 'impostor' ? d.facets[Number(index)] : undefined;
      if (entry !== undefined) Object.assign(entry, { sheet: input.sheet, origin });
    }
    for (const key of Object.keys(cells)) {
      const { slot, entry } = findSlot(p, key);
      entry.slots[String(slot.tile)] = input.image;
    }
    repoint(before, input.sheet, input.image);
    checkPuppet(draft, ctx);
    return { result: { sheet: input.sheet, entries: [...origins.keys()] }, warnings: [], inverse };
  },
};
